import {format, parseISO} from 'date-fns'
import {LaborProposalModel, LaborProposalShiftModel} from '../../core/_models'

/**
 * The live availability check on an opened recommendation.
 *
 * A recommendation is generated overnight from the availability of that moment.
 * When a manager marks somebody unavailable during the day and then opens it,
 * the stored "availability fit" still says 100% — the backend re-reads
 * availability on every open, and these helpers turn that into what the modal
 * shows: one line per clash over the week and a red badge on each such shift.
 */

/**
 * One line per clashing shift — "Destiny Bannerman unavailable: Wed Oct 14
 * 19:30–03:00" — or [] when nothing clashes (or the check could not run).
 */
export const unavailableLines = (detail: LaborProposalModel | null): string[] => {
  if (!detail?.availability_now?.unavailable_shift_count) return []
  return (detail.shifts || [])
    .filter((s) => s.availability_conflict === 'unavailable')
    .map(
      (s) =>
        `${s.employee_name || 'Unassigned'} unavailable: ` +
        `${format(parseISO(s.start_time), 'EEE MMM d HH:mm')}–${format(
          parseISO(s.end_time),
          'HH:mm'
        )}`
    )
}

/**
 * One line per broken staffing rule — "TEST crew max days: Each Crew must work
 * at most 1 day per week — Leah Price, Keanna Moss, Eden Hewett +34 more".
 */
export const ruleLines = (detail: LaborProposalModel | null, shown = 3): string[] =>
  (detail?.rules_now || []).map((r) => {
    const head = r.sentence ? `${r.name}: ${r.sentence}` : r.name
    if (!r.employees.length) return `${head} (${r.violations}x)`
    const extra = r.employees.length - shown
    const who = r.employees.slice(0, shown).join(', ') + (extra > 0 ? ` +${extra} more` : '')
    return `${head} — ${who}`
  })

/** The badge class for one shift: a clash outranks "unassigned". */
export const shiftBadgeClass = (shift: LaborProposalShiftModel): string => {
  if (shift.availability_conflict === 'unavailable') return 'badge-light-danger'
  if (!shift.employee_id) return 'badge-light-warning'
  return 'badge-light-primary'
}

/** The tooltip line for a clash, or undefined. */
export const conflictLabel = (shift: LaborProposalShiftModel): string | undefined => {
  if (shift.availability_conflict === 'unavailable') {
    return `${shift.availability_conflict_hours ?? 0} hrs inside an unavailable window`
  }
  if (shift.availability_conflict === 'not_preferred') {
    return `${shift.availability_conflict_hours ?? 0} hrs inside a not-preferred window`
  }
  return undefined
}
